import { useNavigate } from "@tanstack/react-router";
import { LayoutGrid, SearchX, Table as TableIcon, X } from "lucide-react";
import { useMemo } from "react";
import { CategoryTabs } from "@/components/discovery/CategoryTabs";
import { FilterPopover } from "@/components/discovery/FilterPopover";
import { ErrorState } from "@/components/shared/error-state";
import { SkillCard } from "@/components/skills/SkillCard";
import { SkillTable } from "@/components/skills/SkillTable";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useSkillFilters } from "@/hooks/use-skill-filters";
import type { DiscoverySearch, SortKey } from "@/lib/skills/search-params";
import {
  applySort,
  presetKeyFor,
  SORT_PRESET_LABELS,
  SORT_PRESETS,
  type SortDir,
} from "@/lib/skills/sort";

export function AllSkillsView({ search }: { search: DiscoverySearch }) {
  const navigate = useNavigate();
  const {
    base,
    filtered,
    counts,
    state,
    update,
    activePills,
    activeFilterCount,
    clearAll,
    isLoading,
    isError,
    refetch,
  } = useSkillFilters(search);

  const gridRows = useMemo(
    () => applySort(filtered, state.sort, state.dir),
    [filtered, state.sort, state.dir],
  );
  // Table base is relevance-ordered (the resting state); TanStack sorts columns on top.
  const tableRows = useMemo(() => applySort(filtered, "relevance", "desc"), [filtered]);

  function setSort(sort: SortKey, dir: SortDir) {
    update({
      sort: sort === "relevance" ? undefined : sort,
      dir: dir === "desc" ? undefined : "asc",
    });
  }

  const presetKey = presetKeyFor(state.sort, state.dir);
  const openSkill = (id: string) => void navigate({ to: "/skill/$id", params: { id } });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-fg-link">
          Internal Library
        </div>
        <h1 className="mt-1 font-display text-[30px] font-bold text-fg-1">All Skills</h1>
        <p className="mt-2 max-w-2xl text-fg-2">
          Search, filter, and sort the full library. Click any column header to sort the table.
        </p>
      </div>

      <CategoryTabs
        facets={counts}
        total={base.length}
        value={state.cat}
        onChange={(next) => update({ cat: next ?? undefined })}
      />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-fg-2">
          {filtered.length} skill{filtered.length !== 1 ? "s" : ""}
          {state.q ? (
            <>
              {" "}
              for “<b className="text-fg-1">{state.q}</b>”
            </>
          ) : null}
        </span>
        {activePills.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={p.onClear}
            className="inline-flex items-center gap-1 rounded-full bg-bg-accent px-2 py-0.5 text-xs font-medium text-blue hover:bg-blue/10"
          >
            {p.label}
            <X className="size-3" />
          </button>
        ))}
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs font-medium text-fg-link hover:underline"
          >
            Clear all
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <FilterPopover
            state={state}
            counts={counts}
            update={update}
            activeCount={activeFilterCount}
            onClearAll={clearAll}
          />
          {state.view === "grid" && (
            <Select
              value={presetKey ?? "custom"}
              onValueChange={(v) => {
                const p = SORT_PRESETS[v];
                if (p) setSort(p.sort, p.dir);
              }}
            >
              <SelectTrigger className="h-9 w-[170px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {presetKey === null && (
                  <SelectItem value="custom" disabled>
                    Custom
                  </SelectItem>
                )}
                {Object.keys(SORT_PRESETS).map((k) => (
                  <SelectItem key={k} value={k}>
                    {SORT_PRESET_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <ToggleGroup
            type="single"
            value={state.view}
            onValueChange={(v) => v && update({ view: v === "grid" ? "grid" : undefined })}
          >
            <ToggleGroupItem value="table" aria-label="Table view">
              <TableIcon className="size-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="grid" aria-label="Grid view">
              <LayoutGrid className="size-4" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-44 rounded-lg" />
          ))}
        </div>
      )}

      {isError && <ErrorState onRetry={() => void refetch()} />}

      {!isLoading && !isError && filtered.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border-2 bg-white py-16 text-center">
          <SearchX className="size-8 text-fg-3" />
          <p className="text-sm font-medium text-fg-2">No skills match your filters</p>
          <Button variant="outline" size="app" onClick={clearAll}>
            Clear filters
          </Button>
        </div>
      )}

      {!isLoading && !isError && filtered.length > 0 && state.view === "grid" && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {gridRows.map((s) => (
            <SkillCard key={s.id} skill={s} />
          ))}
        </div>
      )}

      {!isLoading && !isError && filtered.length > 0 && state.view === "table" && (
        <SkillTable
          rows={tableRows}
          sort={state.sort}
          dir={state.dir}
          onSortChange={setSort}
          onOpen={openSkill}
        />
      )}
    </div>
  );
}
